/**
 * UGC 视频数据适配工具
 *
 * 跨业务（CreativeTrends Drawer / UgcCreativeTrends 列表与详情）共享，
 * 放在 marketing/utils 公共层，避免业务页之间形成反向依赖。
 */

/**
 * UGC Video 通用项（feeds 层 VideoItem）
 *
 * 字段对齐 specs/UGC社媒热门创意详情页/api/list.md & related_videos.md。
 *
 * 视频翻译切换三态：
 *   - origin: `title` / `description`
 *   - en:     `title_en` / `description_en`
 *   - zh:     `title_cn` / `description_cn`（注意后端用 `_cn` 而不是 `_zh`）
 */
export interface IUgcVideoItem {
  video_url: string;
  title?: string;
  title_cn?: string;
  title_en?: string;
  video_cover?: string;
  channel_code?: string;
  content_type?: string;
  description?: string;
  description_cn?: string;
  description_en?: string;
  tweets_view?: number;
  tweets_like?: number;
  tweets_reply?: number;
  tweets_retweet?: number;
  tweets_unlike?: number;
  /** 互动量 = retweet + reply + like + unlike（行级负值视为 0） */
  engagement?: number;
  follower_number?: number;
  duration?: number;
  comment_uin?: string;
  comment_time?: string;
}

/**
 * IUgcVideoItem → VideoCard 组件 props 形状
 *
 * 在数据层（hook / service）统一做"接口字段 → UI 字段"映射，
 * 避免每个消费者各自再写一遍 inline 映射。
 *
 * ⚠️ 视频翻译切换字段对齐 `DBOM_CONTENT.videoTitle` 的 VideoTitleMap：
 *   - origin → `video_title`（取自接口 `title`）
 *   - en     → `video_title_en`（取自接口 `title_en`）
 *   - zh     → `video_title_zh`（取自接口 `title_cn` —— 后端命名差异，这里在 adapter 层抹平）
 */
export function toVideoCardItem(item: IUgcVideoItem): any {
  return {
    ...item,
    url: item.video_url,
    cover: item.video_cover,
    views: item.tweets_view || 0,
    likes: item.tweets_like || 0,
    comments: item.tweets_reply || 0,
    duration: item.duration || 0,
    time: item.comment_time,
    channel: item.channel_code?.toLowerCase?.() || item.channel_code,
    username: item.channel_code,
    title: item.title,
    video_title: item.title,
    video_title_en: item.title_en,
    video_title_zh: item.title_cn,
  };
}

/**
 * UGC 视频排序选项（Drawer / 详情页 Related Videos 共用）
 */
export const VIDEO_SORT_OPTIONS = [
  { text: 'Date', value: 'video_release_time' },
  { text: 'Views', value: 'tweets_view' },
  { text: 'Engagement', value: 'engagement' },
  { text: 'Likes', value: 'tweets_like' },
];
